import { Skeleton, Table } from "antd";
import { ColumnProps } from "antd/lib/table";
import { DORMITORY_DETAIL_ROUTE, DORMITORY_MASTER_ROUTE } from "config/route-consts";
import { Device } from "models/Device";
import { Scene } from "models/Scene";
import moment from "moment";
import React from "react";
import { Link, useHistory } from "react-router-dom";
import { roomRepository } from "repositories/room-repository";
import "./RoomDetail.scss";
import { openOnRowClick } from "helpers/row-click";
import {
  DeviceDetailModal,
  useDeviceDetailModal,
} from "views/HomeControllerView/HomeControllerDetail/DeviceDetailModal";
import useRoomDetail from "./RoomDetailHook";

const WEEK_DAYS: [keyof Scene, string][] = [
  ["eachMonday", "T2"],
  ["eachTuesday", "T3"],
  ["eachWednesday", "T4"],
  ["eachThursday", "T5"],
  ["eachFriday", "T6"],
  ["eachSaturday", "T7"],
  ["eachSunday", "CN"],
];

// "Hẹn giờ 22:30 · T2, T4, T6" / "Hằng ngày"; cảnh chạy tay thì "Chạy thủ công".
function describeScene(scene: Scene) {
  const days = WEEK_DAYS.filter(([key]) => scene[key]).map(([, label]) => label);
  const repeat = days.length === 7 ? "Hằng ngày" : days.join(", ");
  if (!scene.timer) return repeat || "Chạy thủ công";
  return [`Hẹn giờ ${moment(scene.timer).format("HH:mm")}`, repeat].filter(Boolean).join(" · ");
}

// Chi tiết Phòng: cùng khuôn với chi tiết HC và chi tiết Nhà (đầu trang + lưới 2fr/1fr).
function RoomDetail() {
  const { model, loadError } = useRoomDetail(roomRepository.get);
  const {
    visible: visibleDeviceDetailModal,
    selectedDevice,
    openDeviceDetailModal,
    closeDeviceDetailModal,
  } = useDeviceDetailModal();
  const history = useHistory();

  const devices = model.devices ?? [];
  const scenes = model.scenes ?? [];

  const handleGoBack = React.useCallback(() => {
    history.push(DORMITORY_MASTER_ROUTE);
  }, [history]);

  const deviceColumns: ColumnProps<Device>[] = React.useMemo(
    () => [
      {
        title: "Thiết bị",
        key: "name",
        render: (_value: string, device) => (
          <div className="room-detail__cell">
            {/* ID thiết bị (GUID) quá dài cho một cột: rê chuột lên tên để xem. */}
            <div className="room-detail__strong room-detail__truncate" title={`ID: ${device.id}`}>
              {device.name || "—"}
            </div>
            {device.macAddress && (
              <div className="room-detail__sub room-detail__mono">{device.macAddress}</div>
            )}
          </div>
        ),
      },
      {
        title: "Loại",
        key: "deviceType",
        render: (_value: string, device) => device.deviceType?.name,
      },
      {
        title: "Home Controller",
        key: "homeController",
        render: (_value: string, device) => device.homeController?.name,
      },
      {
        title: "Phiên bản FW",
        dataIndex: "firmwareVersion",
        render: (value: string) => <span className="tabular-nums">{value}</span>,
      },
    ],
    []
  );

  // Hiển thị lỗi khi phòng không tồn tại hoặc đã bị xóa
  if (loadError) {
    return (
      <div className="room-detail">
        <div className="room-detail__error">
          <i className="tio-room_outlined" />
          <h2>Phòng không tồn tại</h2>
          <p>Phòng này đã bị xóa hoặc không tồn tại trong hệ thống.</p>
          <button className="room-detail__back-btn" onClick={handleGoBack}>
            <i className="tio-arrow_backward" />
            Quay lại danh sách
          </button>
        </div>
      </div>
    );
  }

  // Chưa có dữ liệu thì hiện khung xám thay vì các khối rỗng nhấp nháy.
  if (!model.id) {
    return (
      <div className="room-detail">
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    );
  }

  return (
    <div className="room-detail">
      <div className="room-detail__head">
        {model.dormitoryId ? (
          <Link to={`${DORMITORY_DETAIL_ROUTE}?id=${model.dormitoryId}`} className="page-back">
            <i className="tio-chevron_left" aria-hidden />
            Chi tiết nhà
          </Link>
        ) : (
          <Link to={DORMITORY_MASTER_ROUTE} className="page-back">
            <i className="tio-chevron_left" aria-hidden />
            Danh sách nhà
          </Link>
        )}
        <h1 className="room-detail__title">{model.name || "—"}</h1>
        <p className="room-detail__meta">
          {devices.length} thiết bị · {scenes.length ? `${scenes.length} cảnh` : "chưa có cảnh"}
        </p>
      </div>

      <div className="room-detail__grid">
        <section className="room-detail__card">
          <div className="room-detail__card-head">
            <h2 className="room-detail__card-title">
              Thiết bị <span className="room-detail__count">· {devices.length}</span>
            </h2>
          </div>
          {devices.length === 0 ? (
            <p className="room-detail__empty">Phòng chưa có thiết bị nào</p>
          ) : (
            <>
              <Table
                className="room-detail__table"
                rowKey="id"
                columns={deviceColumns}
                dataSource={devices}
                onRow={(device) => openOnRowClick(() => openDeviceDetailModal(device))}
                pagination={false}
                scroll={{ x: "max-content" }}
              />
              <ul className="room-detail__rows room-detail__rows--mobile room-detail__rows--clickable">
                {devices.map((device) => (
                  <li key={device.id} onClick={() => openDeviceDetailModal(device)}>
                    <span className="room-detail__obj-icon">
                      <i className="tio-plug_outlined" />
                    </span>
                    <div className="room-detail__cell">
                      <div className="room-detail__strong room-detail__truncate">{device.name || "—"}</div>
                      <div className="room-detail__sub">
                        {[device.deviceType?.name, device.homeController?.name, device.firmwareVersion && `FW ${device.firmwareVersion}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="room-detail__card">
          <div className="room-detail__card-head">
            <h2 className="room-detail__card-title">
              Cảnh <span className="room-detail__count">· {scenes.length}</span>
            </h2>
          </div>
          {scenes.length === 0 ? (
            <p className="room-detail__empty">Phòng chưa có cảnh nào</p>
          ) : (
            <ul className="room-detail__rows">
              {scenes.map((scene) => (
                <li key={scene.id}>
                  <span className="room-detail__obj-icon">
                    <i className="tio-movie" />
                  </span>
                  <div className="room-detail__cell">
                    <div className="room-detail__strong room-detail__truncate">{scene.name || "—"}</div>
                    <div className="room-detail__sub">{describeScene(scene)}</div>
                  </div>
                  {scene.isFavoriteScene && (
                    <i className="tio-star room-detail__favorite" title="Cảnh yêu thích" />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <DeviceDetailModal
        device={selectedDevice}
        visible={visibleDeviceDetailModal}
        onClose={closeDeviceDetailModal}
      />
    </div>
  );
}

export default RoomDetail;
